import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";

const line = {
  initial: { y: "110%" },
  animate: (i = 0) => ({
    y: 0,
    transition: { delay: 0.15 + i * 0.12, duration: 1.1, ease: [0.76, 0, 0.24, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // Pin Aesthetic./Painless. in place while page scrolls, then fade out fast
  const { scrollY: winY } = useScroll();
  const pinY = useTransform(winY, (v) => v);
  const wordOpacity = useTransform(winY, [0, 140], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-28 md:pt-36 pb-24 overflow-hidden"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 lg:px-16 grid grid-cols-12 gap-6 md:gap-10">
        {/* Left: eyebrow + massive headline */}
        <motion.div style={{ y: textY, opacity }} className="col-span-12 lg:col-span-8 relative z-10">
          <div className="flex items-center gap-4 mb-10 md:mb-14">
            <span className="w-10 h-px bg-ink" />
            <span className="eyebrow">Est. Edamuttam · Kerala</span>
            <span className="font-malayalam text-[13px] text-ink/60 leading-none">എടമുട്ടം · കേരള</span>
          </div>

          <h1
            data-testid="hero-heading"
            className="relative font-serif font-light text-ink text-[15vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.9] tracking-tighter"
          >
            {/* Google Maps button — floats to the right of the three lines on desktop */}
            <motion.a
              href="https://maps.app.goo.gl/fR91pXjocvQoBhz79?g_st=ic"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-maps-button"
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 flex-col items-center gap-3 group z-20"
              aria-label="Open Apex Dental Care location in Google Maps"
            >
              <div className="relative w-24 h-24 rounded-full border border-ink/25 bg-white/60 backdrop-blur-sm flex items-center justify-center transition-all duration-500 group-hover:bg-ink group-hover:border-ink group-hover:scale-105 shadow-[0_10px_30px_-10px_rgba(10,30,54,0.15)]">
                <MapPin size={30} strokeWidth={1.25} className="text-ink transition-colors duration-500 group-hover:text-porcelain" />
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full" style={{ background: "#E97934" }} />
              </div>
              <span className="text-[11px] tracking-[0.24em] uppercase text-ink text-center leading-tight font-light">
                View<br />on Maps
              </span>
            </motion.a>

            <motion.span className="mask-line" style={{ y: pinY, opacity: wordOpacity, display: "block" }}>
              <motion.span variants={line} initial="initial" animate="animate" custom={0} className="block" style={{ color: "#D2B48C" }}>
                Aesthetic.
              </motion.span>
            </motion.span>
            <span className="mask-line">
              <motion.span variants={line} initial="initial" animate="animate" custom={1} className="block italic text-taupe">
                Precision.
              </motion.span>
            </span>
            <motion.span className="mask-line" style={{ y: pinY, opacity: wordOpacity, display: "block" }}>
              <motion.span variants={line} initial="initial" animate="animate" custom={2} className="block" style={{ color: "#D2B48C" }}>
                Painless.
              </motion.span>
            </motion.span>
          </h1>

          {/* Maps chip — mobile fallback (below H1) */}
          <motion.a
            href="https://maps.app.goo.gl/fR91pXjocvQoBhz79?g_st=ic"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-maps-button-mobile"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="lg:hidden mt-6 inline-flex items-center gap-3 border border-ink/30 rounded-full px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase text-ink hover:bg-ink hover:text-porcelain transition-colors duration-500"
          >
            <MapPin size={14} strokeWidth={1.5} />
            <span>View on Maps</span>
          </motion.a>

          <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="col-span-12 md:col-span-6 text-ink/80 text-base md:text-lg leading-relaxed font-light max-w-[46ch]"
              data-testid="hero-subtitle"
            >
              Apex Dental Care — a quiet clinic in Edamuttam
              dedicated to specialist endodontics, implants and cosmetic dentistry.
              Every visit designed to feel less like a procedure, more like therapy.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
              className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col gap-6"
            >
              <a
                href="tel:+919961717181"
                data-testid="hero-book-cta"
                className="group inline-flex items-center justify-between bg-ink text-porcelain pl-6 pr-4 py-5 text-[12px] tracking-[0.24em] uppercase w-full max-w-[320px] hover:bg-taupe transition-colors duration-500"
              >
                <span>Call to Book</span>
                <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1.5">→</span>
              </a>
              <a
                href="tel:+919961717181"
                className="text-ink/80 text-sm tracking-[0.18em] uppercase link-underline w-fit"
                data-testid="hero-phone-cta"
              >
                +91 99617 17181
              </a>
            </motion.div>
          </div>
        </motion.div>

        {/* Right: clipped parallax image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1.2 }}
          className="col-span-12 lg:col-span-4 relative z-0 lg:pl-6"
          data-testid="hero-image-frame"
        >
          <div className="relative aspect-[3/4] w-full overflow-hidden">
            <motion.img
              src="https://customer-assets.emergentagent.com/job_root-canal-experts-2/artifacts/kaibsuwt_abc00fc7-685d-437c-ae7a-7f266b1c6e6a.webp"
              alt="APEX Dental Care storefront on Edamuttam Kattoor Road"
              style={{ scale: imgScale, y: imgY }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-porcelain">
              <div>
                <div className="text-[10px] tracking-[0.32em] uppercase opacity-80">The Clinic</div>
                <div className="font-serif italic text-xl mt-1">Edamuttam · Kerala</div>
              </div>
              <div className="text-[10px] tracking-[0.28em] uppercase opacity-80 text-right">
                Ground Floor <br /> Kodanchery Arcade
              </div>
            </div>
          </div>

          {/* small stats card, editorial */}
          <div className="mt-6 grid grid-cols-2 gap-6 border-t border-bone pt-6">
            <div>
              <div className="font-serif text-3xl leading-none">14+</div>
              <div className="eyebrow mt-2">Years · Specialist Care</div>
            </div>
            <div>
              <div className="font-serif text-3xl leading-none">500+</div>
              <div className="eyebrow mt-2">Root Canals Performed</div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="block w-px h-10 bg-ink animate-blink" />
      </motion.div>
    </section>
  );
}
